import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import ToolTipBox from '@/components/Common/ToolTipBox.jsx';

import { onPrimaryColor } from '@/styles/darkHero.ts';

interface Option {
    label: string;
    isReturning: boolean;
    disabled?: boolean;
    disabledHint?: string;
}

interface Props {
    options: Option[];
    isReturning: boolean;
    onChange: (isReturning: boolean) => void;
}

const useStyles = makeStyles((theme: any) => ({
    group: {
        display: 'inline-flex',
        alignSelf: 'flex-start',
        padding: '0.25rem',
        gap: '0.25rem',
        background: theme.palette.common.lightGray,
        borderRadius: theme.radius.field,
    },
    option: {
        appearance: 'none',
        border: 'none',
        padding: '0.5rem 1rem',
        fontFamily: 'inherit',
        fontSize: '0.875rem',
        background: 'transparent',
        borderRadius: theme.radius.default,
        color: theme.palette.common.darkGrey,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 0.2s ease',
        '&:hover:not(:disabled)': {
            background: theme.palette.common.white,
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.palette.common.darkGrey}`,
            outlineOffset: '2px',
        },
        '&[aria-disabled="true"]': {
            cursor: 'default',
            opacity: 0.4,
        },
    },
    disabled: {
        opacity: 0.4,
    },
    hint: {
        position: 'absolute',
        width: 1,
        height: 1,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
    },
    active: {
        background: theme.palette.common.yellow,
        color: onPrimaryColor(theme),
        fontWeight: theme.weight.heading,
        '&:hover:not(:disabled)': {
            background: theme.palette.common.yellow,
        },
    },
}));

const hintId = (option: Option) => `patient-type-hint-${option.isReturning ? 'returning' : 'new'}`;

export const TimeslotPatientType = ({ options, isReturning, onChange }: Props) => {
    const classes = useStyles();

    return (
        <div className={classes.group} role="group" data-testid="booking-patient-type">
            {options.map((option) => {
                const button = (
                    <button
                        type="button"
                        aria-disabled={option.disabled}
                        aria-describedby={option.disabled && option.disabledHint ? hintId(option) : undefined}
                        aria-pressed={option.isReturning === isReturning}
                        className={clsx(
                            classes.option,
                            option.disabled && classes.disabled,
                            option.isReturning === isReturning && classes.active
                        )}
                        onClick={() => !option.disabled && onChange(option.isReturning)}
                        data-testid={`booking-patient-type-${option.isReturning ? 'returning' : 'new'}`}
                    >
                        {option.label}
                    </button>
                );

                return option.disabled && option.disabledHint ? (
                    <ToolTipBox key={option.label} text={option.disabledHint} props={{}}>
                        <span>
                            {button}
                            <span id={hintId(option)} className={classes.hint}>
                                {option.disabledHint}
                            </span>
                        </span>
                    </ToolTipBox>
                ) : (
                    <span key={option.label}>{button}</span>
                );
            })}
        </div>
    );
};
